/**
 * `modules/core/routes.ts` — the three cross-module reads the client portal opens on.
 *
 * `client/dashboard.html` is one call by design: metrics, allowances, decisions and
 * upcoming come back together so the page renders once rather than in four stages
 * (`readmodels/client-dashboard.ts`). The two narrower hooks exist because the
 * requests page wants the decisions **and their counts** without the metrics, and
 * the report document wants the timeline without either.
 */

import { useApiQuery } from '../hooks.js'
import { queryKeys } from '../keys.js'

/** `GET /api/clients/me/dashboard` — the whole of `client/dashboard.html`. */
export function useDashboard() {
  return useApiQuery({
    key: queryKeys.core.dashboard(),
    request: (api) => api.rest.clients.me.dashboard.$get(),
  })
}

/**
 * `GET /api/clients/me/dashboard/decisions` — the merged four kinds, with counts.
 *
 * The options are here because the **sidebar** reads this too
 * (`shell/useNavBadges.ts`): a nav badge must not take the page down when it cannot
 * be counted, and it must not ask at all for a staff principal who is between
 * `stop-impersonating` and the navigation out of this layout.
 */
export function useDecisions(options: { enabled?: boolean; inline?: boolean } = {}) {
  return useApiQuery({
    key: queryKeys.core.decisions(),
    request: (api) => api.rest.clients.me.dashboard.decisions.$get(),
    ...(options.enabled === undefined ? {} : { enabled: options.enabled }),
    ...(options.inline === undefined ? {} : { inline: options.inline }),
  })
}

/** `GET /api/clients/me/dashboard/upcoming` — the timeline on its own. */
export function useUpcoming() {
  return useApiQuery({
    key: queryKeys.core.upcoming(),
    request: (api) => api.rest.clients.me.dashboard.upcoming.$get(),
  })
}
